import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Download, FileText, FileSpreadsheet, Image, Share2, Link2, Copy, Check,
  ArrowLeft, Calendar, ChevronDown, Eye, EyeOff, Trash2, Clock, Users,
  Lock, Unlock, ExternalLink, BarChart3, PieChart, TrendingUp, Settings
} from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, PieChart as RechartPieChart, Pie, Cell
} from 'recharts';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const COLORS = ['#8b5cf6', '#10b981', '#3b82f6'];

function ExportSharePage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('export');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Export state
  const [reportTemplate, setReportTemplate] = useState('executive_summary');
  const [dateRange, setDateRange] = useState('90d');
  const [exportFormat, setExportFormat] = useState('pdf');
  const [reportPreview, setReportPreview] = useState(null);
  
  // Share state
  const [shares, setShares] = useState([]);
  const [showCreateShare, setShowCreateShare] = useState(false);
  const [newShare, setNewShare] = useState({
    name: '',
    expiration: '30d',
    password: '',
    showPillars: true,
    showTrend: true
  });
  const [copiedId, setCopiedId] = useState(null);

  const reportRef = useRef(null);

  useEffect(() => {
    if (activeTab === 'share') {
      loadShares();
    }
  }, [activeTab]);

  const loadShares = async () => {
    try {
      const response = await axios.get('/shares/');
      setShares(response.data);
    } catch (err) {
      console.error('Failed to load shares:', err);
    }
  };

  const getDateParams = () => {
    const end = new Date();
    const start = new Date();
    
    switch (dateRange) {
      case '30d': start.setDate(end.getDate() - 30); break;
      case '90d': start.setDate(end.getDate() - 90); break;
      case '1y': start.setFullYear(end.getFullYear() - 1); break;
      default: start.setDate(end.getDate() - 90);
    }
    
    return {
      date_range_start: start.toISOString(),
      date_range_end: end.toISOString()
    };
  };

  const loadReportPreview = async () => {
    try {
      setLoading(true);
      setError('');
      
      const params = getDateParams();
      const response = await axios.post('/exports/reports/preview', {
        template: reportTemplate,
        ...params
      });
      
      setReportPreview(response.data);
    } catch (err) {
      setError('Failed to load report preview');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleExportPDF = async () => {
    try {
      setLoading(true);
      setError('');
      
      // First load preview data if not already loaded
      if (!reportPreview) {
        await loadReportPreview();
      }
      
      // Generate PDF from the preview element
      const element = reportRef.current;
      if (!element) {
        throw new Error('Report element not found');
      }
      
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false
      });
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
      const imgX = (pdfWidth - imgWidth * ratio) / 2;
      
      pdf.addImage(imgData, 'PNG', imgX, 10, imgWidth * ratio, imgHeight * ratio);
      pdf.save(`trust_index_report_${new Date().toISOString().split('T')[0]}.pdf`);
      
      setSuccess('PDF exported successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to export PDF');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = async () => {
    try {
      setLoading(true);
      const params = getDateParams();
      
      const response = await axios.post('/exports/csv', params, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `trust_index_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      setSuccess('CSV exported successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to export CSV');
    } finally {
      setLoading(false);
    }
  };

  const handleExportExcel = async () => {
    try {
      setLoading(true);
      const params = getDateParams();
      
      const response = await axios.post('/exports/excel', {
        format: 'xlsx',
        ...params,
        include_raw: true,
        include_normalized: true
      });
      
      // Convert sheets data to downloadable format
      // For now, we'll download as JSON - in production use xlsx library
      const blob = new Blob([JSON.stringify(response.data.sheets, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `trust_index_${new Date().toISOString().split('T')[0]}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      setSuccess('Excel data exported! (JSON format)');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to export Excel');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateShare = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await axios.post('/shares/', {
        name: newShare.name || null,
        expiration: newShare.expiration,
        password: newShare.password || null,
        config: {
          show_pillars: newShare.showPillars,
          show_trend: newShare.showTrend,
          show_comparison: false,
          show_departments: false
        }
      });
      
      setShares([response.data, ...shares]);
      setShowCreateShare(false);
      setNewShare({ name: '', expiration: '30d', password: '', showPillars: true, showTrend: true });
      setSuccess('Share link created!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create share');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteShare = async (shareId) => {
    if (!window.confirm('Are you sure you want to delete this share link?')) return;
    
    try {
      await axios.delete(`/shares/${shareId}`);
      setShares(shares.filter(s => s.id !== shareId));
      setSuccess('Share link deleted');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to delete share');
    }
  };

  const copyToClipboard = async (url, id) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleDateString();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="p-2 hover:bg-gray-100 rounded-lg transition"
                data-testid="back-button"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900" data-testid="page-title">
                  Export & Share
                </h1>
                <p className="text-sm text-gray-500">Generate reports and share your dashboard</p>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-4 mt-4 border-t border-gray-100 pt-4">
            <button
              onClick={() => setActiveTab('export')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition font-medium ${
                activeTab === 'export'
                  ? 'bg-purple-100 text-purple-700'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
              data-testid="export-tab"
            >
              <Download className="w-4 h-4" />
              Export Reports
            </button>
            <button
              onClick={() => setActiveTab('share')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition font-medium ${
                activeTab === 'share'
                  ? 'bg-purple-100 text-purple-700'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
              data-testid="share-tab"
            >
              <Share2 className="w-4 h-4" />
              Share Dashboard
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Alerts */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}
        {success && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-6">
            {success}
          </div>
        )}

        {/* Export Tab */}
        {activeTab === 'export' && (
          <div className="space-y-6">
            {/* Export Options */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-6">Export Options</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Template Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Report Template
                  </label>
                  <select
                    value={reportTemplate}
                    onChange={(e) => setReportTemplate(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    data-testid="template-select"
                  >
                    <option value="executive_summary">Executive Summary (2-4 pages)</option>
                    <option value="detailed_analysis">Detailed Analysis (8-15 pages)</option>
                    <option value="custom">Custom Report</option>
                  </select>
                </div>

                {/* Date Range */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Date Range
                  </label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <select
                      value={dateRange}
                      onChange={(e) => setDateRange(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                      data-testid="date-range-select"
                    >
                      <option value="30d">Last 30 Days</option>
                      <option value="90d">Last 90 Days</option>
                      <option value="1y">Last Year</option>
                    </select>
                  </div>
                </div>

                {/* Format Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Export Format
                  </label>
                  <select
                    value={exportFormat}
                    onChange={(e) => setExportFormat(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    data-testid="format-select"
                  >
                    <option value="pdf">PDF Report</option>
                    <option value="csv">CSV Data</option>
                    <option value="excel">Excel Workbook</option>
                  </select>
                </div>
              </div>

              {/* Export Buttons */}
              <div className="flex gap-4 mt-6">
                <button
                  onClick={loadReportPreview}
                  disabled={loading}
                  className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
                  data-testid="preview-button"
                >
                  <Eye className="w-4 h-4" />
                  Preview
                </button>
                
                <button
                  onClick={() => {
                    if (exportFormat === 'pdf') handleExportPDF();
                    else if (exportFormat === 'csv') handleExportCSV();
                    else handleExportExcel();
                  }}
                  disabled={loading}
                  className="flex items-center gap-2 px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition disabled:opacity-50"
                  data-testid="export-button"
                >
                  <Download className="w-4 h-4" />
                  {loading ? 'Exporting...' : `Export ${exportFormat.toUpperCase()}`}
                </button>
              </div>
            </div>

            {/* Quick Export Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div 
                onClick={handleExportPDF}
                className="bg-white rounded-xl shadow-lg p-6 cursor-pointer hover:shadow-xl transition border-2 border-transparent hover:border-purple-200"
                data-testid="quick-export-pdf"
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className="p-3 bg-red-100 rounded-xl">
                    <FileText className="w-6 h-6 text-red-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900">PDF Report</h3>
                    <p className="text-sm text-gray-500">Visual report with charts</p>
                  </div>
                </div>
                <p className="text-xs text-gray-400">Click to export executive summary</p>
              </div>

              <div 
                onClick={handleExportCSV}
                className="bg-white rounded-xl shadow-lg p-6 cursor-pointer hover:shadow-xl transition border-2 border-transparent hover:border-green-200"
                data-testid="quick-export-csv"
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className="p-3 bg-green-100 rounded-xl">
                    <FileSpreadsheet className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900">CSV Export</h3>
                    <p className="text-sm text-gray-500">Raw data download</p>
                  </div>
                </div>
                <p className="text-xs text-gray-400">Click to download trust scores</p>
              </div>

              <div 
                onClick={handleExportExcel}
                className="bg-white rounded-xl shadow-lg p-6 cursor-pointer hover:shadow-xl transition border-2 border-transparent hover:border-blue-200"
                data-testid="quick-export-excel"
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className="p-3 bg-blue-100 rounded-xl">
                    <FileSpreadsheet className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900">Excel Workbook</h3>
                    <p className="text-sm text-gray-500">Multi-sheet analysis</p>
                  </div>
                </div>
                <p className="text-xs text-gray-400">Click to export with all sheets</p>
              </div>
            </div>

            {/* Report Preview */}
            {reportPreview && (
              <div className="bg-white rounded-xl shadow-lg p-6" data-testid="report-preview">
                <h2 className="text-xl font-bold text-gray-900 mb-6">Report Preview</h2>
                
                <div ref={reportRef} className="bg-gray-50 p-8 rounded-xl">
                  {/* Report Header */}
                  {reportPreview.sections?.find(s => s.type === 'header') && (
                    <div className="text-center mb-8 pb-6 border-b border-gray-200">
                      <h1 className="text-3xl font-bold text-gray-900">
                        {reportPreview.sections.find(s => s.type === 'header').title}
                      </h1>
                      <p className="text-xl text-purple-600 mt-2">
                        {reportPreview.sections.find(s => s.type === 'header').subtitle}
                      </p>
                      <p className="text-gray-500 mt-2">
                        {reportPreview.sections.find(s => s.type === 'header').date}
                      </p>
                    </div>
                  )}

                  {/* Summary Section */}
                  {reportPreview.sections?.find(s => s.type === 'summary') && (() => {
                    const summary = reportPreview.sections.find(s => s.type === 'summary');
                    return (
                      <div className="mb-8">
                        <h2 className="text-xl font-bold text-gray-900 mb-4">Executive Summary</h2>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <div className="bg-white p-4 rounded-lg text-center">
                            <p className="text-3xl font-bold text-purple-600">{summary.trust_score?.toFixed(1)}</p>
                            <p className="text-sm text-gray-500">Trust Score</p>
                          </div>
                          <div className="bg-white p-4 rounded-lg text-center">
                            <p className="text-2xl font-bold text-purple-500">{summary.authenticity_score?.toFixed(1)}</p>
                            <p className="text-sm text-gray-500">Authenticity</p>
                          </div>
                          <div className="bg-white p-4 rounded-lg text-center">
                            <p className="text-2xl font-bold text-green-500">{summary.accountability_score?.toFixed(1)}</p>
                            <p className="text-sm text-gray-500">Accountability</p>
                          </div>
                          <div className="bg-white p-4 rounded-lg text-center">
                            <p className="text-2xl font-bold text-blue-500">{summary.community_score?.toFixed(1)}</p>
                            <p className="text-sm text-gray-500">Community</p>
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Trend Chart */}
                  {reportPreview.sections?.find(s => s.type === 'trend') && (
                    <div className="mb-8">
                      <h2 className="text-xl font-bold text-gray-900 mb-4">Score Trend</h2>
                      <div className="bg-white p-4 rounded-lg h-64">
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={reportPreview.sections.find(s => s.type === 'trend').data}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                            <XAxis 
                              dataKey="date" 
                              tickFormatter={v => new Date(v).toLocaleDateString('en-US', { month: 'short' })}
                              stroke="#9ca3af"
                            />
                            <YAxis domain={[0, 100]} stroke="#9ca3af" />
                            <Tooltip />
                            <Line type="monotone" dataKey="score" stroke="#8b5cf6" strokeWidth={2} dot={false} />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  )}

                  {/* Pillars */}
                  {reportPreview.sections?.find(s => s.type === 'pillars') && (
                    <div className="mb-8">
                      <h2 className="text-xl font-bold text-gray-900 mb-4">Pillar Breakdown</h2>
                      <div className="grid grid-cols-3 gap-4">
                        {reportPreview.sections.find(s => s.type === 'pillars').pillars.map((pillar, idx) => (
                          <div key={pillar.name} className="bg-white p-4 rounded-lg">
                            <div className="flex items-center justify-between mb-2">
                              <span className="font-medium">{pillar.name}</span>
                              <span className="font-bold" style={{ color: COLORS[idx] }}>{pillar.score?.toFixed(1)}</span>
                            </div>
                            <div className="h-2 bg-gray-200 rounded-full">
                              <div 
                                className="h-full rounded-full transition-all"
                                style={{ width: `${pillar.score}%`, backgroundColor: COLORS[idx] }}
                              />
                            </div>
                            <p className="text-xs text-gray-500 mt-2">{pillar.description}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Footer */}
                  <div className="text-center text-sm text-gray-400 pt-6 border-t border-gray-200">
                    Generated by Trust Index Platform • {new Date().toLocaleDateString()}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Share Tab */}
        {activeTab === 'share' && (
          <div className="space-y-6">
            {/* Create Share Button */}
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-900">Shareable Links</h2>
              <button
                onClick={() => setShowCreateShare(true)}
                className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition"
                data-testid="create-share-button"
              >
                <Link2 className="w-4 h-4" />
                Create Share Link
              </button>
            </div>

            {/* Shares List */}
            {shares.length === 0 ? (
              <div className="bg-white rounded-xl shadow-lg p-12 text-center">
                <Share2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-2">No Share Links Yet</h3>
                <p className="text-gray-500 mb-6">Create a shareable link to give others read-only access to your dashboard</p>
                <button
                  onClick={() => setShowCreateShare(true)}
                  className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition"
                >
                  Create Your First Share Link
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {shares.map((share) => (
                  <div 
                    key={share.id} 
                    className={`bg-white rounded-xl shadow-lg p-6 ${share.is_expired ? 'opacity-60' : ''}`}
                    data-testid={`share-card-${share.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-bold text-gray-900">{share.name || 'Unnamed Share'}</h3>
                          {share.is_password_protected && (
                            <span className="flex items-center gap-1 text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded-full">
                              <Lock className="w-3 h-3" /> Password Protected
                            </span>
                          )}
                          {share.is_expired && (
                            <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded-full">
                              Expired
                            </span>
                          )}
                          {!share.is_active && (
                            <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded-full">
                              Deactivated
                            </span>
                          )}
                        </div>
                        
                        <div className="flex items-center gap-2 mb-4">
                          <input
                            type="text"
                            readOnly
                            value={share.share_url}
                            className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-600"
                          />
                          <button
                            onClick={() => copyToClipboard(share.share_url, share.id)}
                            className="p-2 hover:bg-gray-100 rounded-lg transition"
                            title="Copy link"
                          >
                            {copiedId === share.id ? (
                              <Check className="w-5 h-5 text-green-500" />
                            ) : (
                              <Copy className="w-5 h-5 text-gray-500" />
                            )}
                          </button>
                          <a
                            href={share.share_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 hover:bg-gray-100 rounded-lg transition"
                            title="Open link"
                          >
                            <ExternalLink className="w-5 h-5 text-gray-500" />
                          </a>
                        </div>
                        
                        <div className="flex items-center gap-6 text-sm text-gray-500">
                          <span className="flex items-center gap-1">
                            <Eye className="w-4 h-4" />
                            {share.view_count} views
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            Expires: {formatDate(share.expires_at)}
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            Created: {formatDate(share.created_at)}
                          </span>
                        </div>
                      </div>
                      
                      <button
                        onClick={() => handleDeleteShare(share.id)}
                        className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition"
                        title="Delete share"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </main>

      {/* Create Share Modal */}
      {showCreateShare && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-testid="create-share-modal">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-6">Create Share Link</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Link Name (optional)
                </label>
                <input
                  type="text"
                  value={newShare.name}
                  onChange={(e) => setNewShare({ ...newShare, name: e.target.value })}
                  placeholder="e.g., Q4 Report for Board"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  data-testid="share-name-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Expiration
                </label>
                <select
                  value={newShare.expiration}
                  onChange={(e) => setNewShare({ ...newShare, expiration: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  data-testid="share-expiration-select"
                >
                  <option value="7d">7 Days</option>
                  <option value="30d">30 Days</option>
                  <option value="90d">90 Days</option>
                  <option value="never">Never Expires</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Password Protection (optional)
                </label>
                <input
                  type="password"
                  value={newShare.password}
                  onChange={(e) => setNewShare({ ...newShare, password: e.target.value })}
                  placeholder="Leave blank for no password"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  data-testid="share-password-input"
                />
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  What to Show
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={newShare.showPillars}
                    onChange={(e) => setNewShare({ ...newShare, showPillars: e.target.checked })}
                    className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                  />
                  <span className="text-sm text-gray-600">Pillar Scores</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={newShare.showTrend}
                    onChange={(e) => setNewShare({ ...newShare, showTrend: e.target.checked })}
                    className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                  />
                  <span className="text-sm text-gray-600">Trend Chart</span>
                </label>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowCreateShare(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateShare}
                disabled={loading}
                className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition disabled:opacity-50"
                data-testid="create-share-submit"
              >
                {loading ? 'Creating...' : 'Create Link'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ExportSharePage;
